import React from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Collapse,
  Typography,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import SearchIcon from "@mui/icons-material/Search";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import FlagIcon from "@mui/icons-material/Flag";
import AccountCircle from "@mui/icons-material/AccountCircle";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import StackedLineChartIcon from "@mui/icons-material/StackedLineChart";
import { Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { LogoutButton } from "./button-logout";
import "../styles/navbar.css";

export default function Navbar() {
  const { isAuthenticated, user, loginWithRedirect } = useAuth0();

  // Menu: Stocks (desktop)
  const [anchorStocks, setAnchorStocks] = React.useState(null);
  const openStocksMenu = (event) => setAnchorStocks(event.currentTarget);
  const closeStocksMenu = () => setAnchorStocks(null);

  // Menu: Usuário (mobile + desktop avatar)
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const handleOpenUserMenu = (event) => setAnchorElUser(event.currentTarget);
  const handleCloseUserMenu = () => setAnchorElUser(null);

  // Drawer: navegação mobile
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const toggleDrawer = (value) => () => setDrawerOpen(value);

  // Collapse: submenu Stocks dentro do Drawer
  const [stocksCollapseOpen, setStocksCollapseOpen] = React.useState(false);
  const handleToggleStocksCollapse = () =>
    setStocksCollapseOpen((prev) => !prev);

  return (
    <AppBar position="static" color="primary">
      <Toolbar
        sx={{
          minHeight: { xs: 56, sm: 64 },
          px: { xs: 1, sm: 2 },
        }}
      >
        {/* Mobile: Hamburger */}
        <Box sx={{ display: { xs: "flex", sm: "none" }, mr: 1 }}>
          <IconButton
            size="large"
            color="inherit"
            onClick={toggleDrawer(true)}
            aria-label="abrir navegação"
          >
            <MenuIcon />
          </IconButton>
        </Box>

        {/* Desktop: menus principais */}
        <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 1 }}>
          <Button color="inherit" component={Link} to="/">
            Home
          </Button>

          {/* Stocks */}
          <Button
            color="inherit"
            endIcon={<ExpandMore />}
            onClick={openStocksMenu}
            aria-haspopup="true"
            aria-controls="stocks-menu"
          >
            Stocks
          </Button>
          <Menu
            id="stocks-menu"
            anchorEl={anchorStocks}
            open={Boolean(anchorStocks)}
            onClose={closeStocksMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            keepMounted
          >
            <MenuItem component={Link} to="/asx" onClick={closeStocksMenu}>
              Australia
            </MenuItem>
            <MenuItem component={Link} to="/usa" onClick={closeStocksMenu}>
              USA
            </MenuItem>
          </Menu>

          <Button
            color="inherit"
            component={Link}
            to="/search"
            startIcon={<SearchIcon />}
          >
            Search
          </Button>

          {/* Screener */}
          <Button
            color="inherit"
            component={Link}
            to="/filter"
            startIcon={<FilterAltIcon />}
          >
            Screener
          </Button>

          {/* Portfolio */}
          <Button
            color="inherit"
            component={Link}
            to="/portfolio"
            startIcon={<ShowChartIcon />}
          >
            Portfolio
          </Button>

          {/* Rent Invest Simulation */}
          <Button
            color="inherit"
            component={Link}
            to="/rentinvest"
            startIcon={<StackedLineChartIcon />}
          >
            Rent Invest Simulation
          </Button>

          <Button
            color="inherit"
            component={Link}
            to="/about"
            startIcon={<InfoIcon />}
          >
            About
          </Button>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Desktop: usuário */}
        <Box
          sx={{
            display: { xs: "none", sm: "flex" },
            alignItems: "center",
            gap: 1,
          }}
        >
          {!isAuthenticated ? (
            <Button
              onClick={() => loginWithRedirect()}
              variant="contained"
              sx={{
                borderRadius: "8px",
                fontSize: "14px",
                padding: "6px 12px",
                minWidth: 100,
                color: "#fff",
                background:
                  "linear-gradient(90deg, #fb8c00 0%, #f4511e 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #ef6c00 0%, #e64a19 100%)",
                },
              }}
            >
              Sign In
            </Button>
          ) : (
            <>
              <Button color="inherit" component={Link} to="/profile">
                Profile
              </Button>
              <LogoutButton />
            </>
          )}
        </Box>

        {/* Mobile: menu do usuário */}
        <Box sx={{ display: { xs: "flex", sm: "none" }, ml: 0.5 }}>
          <IconButton
            size="large"
            color="inherit"
            onClick={handleOpenUserMenu}
          >
            {user?.picture ? (
              <Avatar
                src={user.picture}
                alt={user?.name || "User"}
                sx={{ width: 28, height: 28 }}
              />
            ) : (
              <AccountCircle />
            )}
          </IconButton>

          <Menu
            anchorEl={anchorElUser}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            keepMounted
          >
            {!isAuthenticated ? (
              <MenuItem
                onClick={() => {
                  handleCloseUserMenu();
                  loginWithRedirect();
                }}
              >
                Sign In
              </MenuItem>
            ) : (
              <>
                <MenuItem
                  component={Link}
                  to="/profile"
                  onClick={handleCloseUserMenu}
                >
                  Profile
                </MenuItem>
                <MenuItem onClick={handleCloseUserMenu}>
                  <LogoutButton />
                </MenuItem>
              </>
            )}
          </Menu>
        </Box>
      </Toolbar>

      {/* Drawer: mobile */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{ sx: { width: 280 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Menu
          </Typography>
        </Box>
        <Divider />
        <List>
          <ListItemButton component={Link} to="/" onClick={toggleDrawer(false)}>
            <ListItemIcon><HomeIcon /></ListItemIcon>
            <ListItemText primary="Home" />
          </ListItemButton>

          {/* Stocks */}
          <ListItemButton onClick={handleToggleStocksCollapse}>
            <ListItemIcon><ShowChartIcon /></ListItemIcon>
            <ListItemText primary="Stocks" />
            {stocksCollapseOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>

          <Collapse in={stocksCollapseOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              <ListItemButton
                sx={{ pl: 4 }}
                component={Link}
                to="/asx"
                onClick={toggleDrawer(false)}
              >
                <ListItemIcon><FlagIcon /></ListItemIcon>
                <ListItemText primary="Australia" />
              </ListItemButton>

              <ListItemButton
                sx={{ pl: 4 }}
                component={Link}
                to="/usa"
                onClick={toggleDrawer(false)}
              >
                <ListItemIcon><FlagIcon /></ListItemIcon>
                <ListItemText primary="USA" />
              </ListItemButton>
            </List>
          </Collapse>

          <ListItemButton
            component={Link}
            to="/search"
            onClick={toggleDrawer(false)}
          >
            <ListItemIcon><SearchIcon /></ListItemIcon>
            <ListItemText primary="Search" />
          </ListItemButton>

          {/* Screener */}
          <ListItemButton
            component={Link}
            to="/filter"
            onClick={toggleDrawer(false)}
          >
            <ListItemIcon><FilterAltIcon /></ListItemIcon>
            <ListItemText primary="Screener" />
          </ListItemButton>

          {/* Portfolio */}
          <ListItemButton
            component={Link}
            to="/portfolio"
            onClick={toggleDrawer(false)}
          >
            <ListItemIcon><ShowChartIcon /></ListItemIcon>
            <ListItemText primary="Portfolio" />
          </ListItemButton>

          {/* Rent Invest Simulation */}
          <ListItemButton
            component={Link}
            to="/rentinvest"
            onClick={toggleDrawer(false)}
          >
            <ListItemIcon><StackedLineChartIcon /></ListItemIcon>
            <ListItemText primary="Rent Invest Simulation" />
          </ListItemButton>

          <ListItemButton
            component={Link}
            to="/about"
            onClick={toggleDrawer(false)}
          >
            <ListItemIcon><InfoIcon /></ListItemIcon>
            <ListItemText primary="About" />
          </ListItemButton>
        </List>
      </Drawer>
    </AppBar>
  );
}
